/* eslint-disable no-bitwise */
import React, { forwardRef, memo, useImperativeHandle, useRef } from "react";

import "./zone-selection-box.css";
import classNames from "classnames";
import { useFeatureFlagToggle } from "../../hooks/useFeatureFlags";
import { Transform } from "../../transform";
import { useAppContext } from "../../hooks/useAppContext";

export enum ZoneSelectionBoxHandlers {
	None = 0,
	TopLeft = 1,
	Top = 2,
	TopRight = 4,
	Left = 8,
	Right = 16,
	BottomLeft = 32,
	Bottom = 64,
	BottomRight = 128,
	All = 255,
	// eslint-disable-next-line @typescript-eslint/prefer-literal-enum-member
	Corners = 1 + 4 + 32 + 128,
	// eslint-disable-next-line @typescript-eslint/prefer-literal-enum-member
	Sides = 2 + 8 + 16 + 64,
}

function updateZoneSelectionBox(
	selectedZone: eXpress.core.IZone | undefined,
	selectionBoxEl: HTMLElement,
	dipToScreenTransform: Transform,
	browserInfo: eXpress.platforms.IBrowserInfo,
) {
	const canSelectZone =
		selectedZone &&
		(eXpress.core.isAnyTextZone(selectedZone) ||
			eXpress.core.isPhotoZone(selectedZone) ||
			eXpress.core.isSymbolZone(selectedZone));

	selectionBoxEl.style.display = canSelectZone ? "block" : "none";

	if (!canSelectZone || !selectedZone) return;

	const rect = dipToScreenTransform.transformRect(selectedZone);

	// On iOS there was sometimes no left outline visible on the selection box.
	// This was caused when using a decimal number as left position in combination with outline styling.
	// To solve this, we do the position with CSS translate, this would also behave more smoothly during animations
	selectionBoxEl.style.left = "0";
	selectionBoxEl.style.top = "0";
	selectionBoxEl.style.width = `${eXpress.math.quant(
		rect.width,
		browserInfo.isIos() ? 1 : 10,
	)}px`;
	selectionBoxEl.style.height = `${eXpress.math.quant(
		rect.height,
		browserInfo.isIos() ? 1 : 10,
	)}px`;
	selectionBoxEl.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0) rotate(${selectedZone.angle}deg)`;
}

function showHandlers(
	selectionBoxEl: HTMLElement,
	handlersToShow: number,
	selectedZone: eXpress.core.IZone | undefined,
) {
	[
		ZoneSelectionBoxHandlers.Top,
		ZoneSelectionBoxHandlers.Left,
		ZoneSelectionBoxHandlers.Right,
		ZoneSelectionBoxHandlers.Bottom,
		ZoneSelectionBoxHandlers.TopLeft,
		ZoneSelectionBoxHandlers.TopRight,
		ZoneSelectionBoxHandlers.BottomLeft,
		ZoneSelectionBoxHandlers.BottomRight,
	].forEach((handle, index) => {
		const el = selectionBoxEl.children[index] as HTMLElement;
		if (el) {
			if ((handlersToShow & handle) > 0) {
				el.style.opacity = "1";
				el.style.pointerEvents = "auto";
			} else {
				el.style.opacity = "0";
				el.style.pointerEvents = "none";
			}

			el.style.cursor = getHandleCursor(handle, selectedZone);
		}
	});
}

/** Depending on the handle an zone rotation, we want to show the correct cursor */
function getHandleCursor(
	handle: ZoneSelectionBoxHandlers,
	selectedZone: eXpress.core.IZone | undefined,
) {
	/**
	 * Quadrants:
	 * >=0° -> <45° = 0
	 * >45°  -> <135° = 1
	 * >135° -> <225° = 2
	 * >225° -> <315° = 3
	 * >315° -> <360° = 0
	 */
	const quadrant = Math.floor((((selectedZone?.angle ?? 0) + 45) % 360) / 90);

	switch (handle) {
		case ZoneSelectionBoxHandlers.TopLeft:
			return quadrant % 2 ? "nesw-resize" : "nwse-resize";
		case ZoneSelectionBoxHandlers.TopRight:
			return quadrant % 2 ? "nwse-resize" : "nesw-resize";
		case ZoneSelectionBoxHandlers.BottomLeft:
			return quadrant % 2 ? "nwse-resize" : "nesw-resize";
		case ZoneSelectionBoxHandlers.BottomRight:
			return quadrant % 2 ? "nesw-resize" : "nwse-resize";
		case ZoneSelectionBoxHandlers.Top:
		case ZoneSelectionBoxHandlers.Bottom:
			return quadrant % 2 ? "ew-resize" : "ns-resize";
		case ZoneSelectionBoxHandlers.Left:
		case ZoneSelectionBoxHandlers.Right:
			return quadrant % 2 ? "ns-resize" : "ew-resize";
		default:
			return "auto";
	}
}

function showDefaultHandlers(
	selectedZone: eXpress.core.IZone | undefined,
	allowedHandles: ZoneSelectionBoxHandlers,
	selectionBoxEl: HTMLElement,
	dipToScreenTransform: Transform | undefined,
) {
	// Keep orientation into account

	// If only one handle, show always
	// Could als be tested with this function: https://www.w3resource.com/javascript-exercises/javascript-math-exercise-13.php
	// const isPowerOf2 = (n: number) => !!n && (n & (n - 1)) === 0;
	if (
		allowedHandles === ZoneSelectionBoxHandlers.TopLeft ||
		allowedHandles === ZoneSelectionBoxHandlers.Top ||
		allowedHandles === ZoneSelectionBoxHandlers.TopRight ||
		allowedHandles === ZoneSelectionBoxHandlers.Left ||
		allowedHandles === ZoneSelectionBoxHandlers.Right ||
		allowedHandles === ZoneSelectionBoxHandlers.BottomLeft ||
		allowedHandles === ZoneSelectionBoxHandlers.Bottom ||
		allowedHandles === ZoneSelectionBoxHandlers.BottomRight
	) {
		showHandlers(selectionBoxEl, allowedHandles, selectedZone); // always show
		return;
	}

	// Hide handlers because of small sizes
	if (!selectedZone || !dipToScreenTransform) return;
	if (dipToScreenTransform) {
		const screenRect = dipToScreenTransform.transformRect(selectedZone);

		const xs = 25;
		const s = 45;

		const isXsWidth = screenRect.width < xs;
		const isSWidth = !isXsWidth && screenRect.width < s;
		const isMWidth = !isXsWidth && !isSWidth;
		const isXsHeight = screenRect.height < xs;
		const isSHeight = !isXsHeight && screenRect.height < s;
		const isMHeight = !isXsHeight && !isSHeight;

		let handles = ZoneSelectionBoxHandlers.TopRight;

		// don't allow top/bottom/left/right handles for zones with an overlay/mask
		if (
			isMWidth &&
			isMHeight &&
			eXpress.core.isPhotoZone(selectedZone) &&
			!!selectedZone.effect
		) {
			handles = ZoneSelectionBoxHandlers.Corners;
		} else {
			if (isMWidth && isSHeight) {
				handles =
					ZoneSelectionBoxHandlers.Corners |
					ZoneSelectionBoxHandlers.Top |
					ZoneSelectionBoxHandlers.Bottom;
			}
			if (isSWidth && isMHeight) {
				handles =
					ZoneSelectionBoxHandlers.Corners |
					ZoneSelectionBoxHandlers.Left |
					ZoneSelectionBoxHandlers.Right;
			}

			// Only one of these will match
			// See schema for more info: https://smartphoto.atlassian.net/browse/CRE-2988?focusedCommentId=518464
			if (isXsWidth && isXsHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isXsWidth && isSHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isXsWidth && isMHeight) handles = ZoneSelectionBoxHandlers.TopRight;

			if (isSWidth && isXsHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isSWidth && isSHeight) handles = ZoneSelectionBoxHandlers.Corners;

			if (isMWidth && isXsHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isMWidth && isSHeight) {
				handles =
					ZoneSelectionBoxHandlers.Corners |
					ZoneSelectionBoxHandlers.Top |
					ZoneSelectionBoxHandlers.Bottom;
			}
			if (isMWidth && isMHeight) handles = ZoneSelectionBoxHandlers.All;
		}

		showHandlers(selectionBoxEl, handles, selectedZone);
	} else {
		// Show all allowed
		showHandlers(selectionBoxEl, ZoneSelectionBoxHandlers.All, selectedZone);
	}
}

export interface IZoneSelectionBoxRef {
	el: HTMLDivElement;

	/** Update the position/size of the selectionBox and handles */
	updateZoneSelectionBox(
		selectedZone: eXpress.core.IZone | undefined,
		dipToScreenTransform: Transform,
	): void;

	/** Automatically show the handles based on available size */
	showDefaultHandlers(
		selectedZone: eXpress.core.IZone | undefined,
		dipToScreenTransform: Transform,
		allowedHandles: ZoneSelectionBoxHandlers,
	): void;
}

interface IZoneSelectionBoxProps {
	/** The handles that the user can drag on the zone selection box */
	handles?: ZoneSelectionBoxHandlers;
}

function CornerHandle({ className, isAllowed }: { className: string; isAllowed: boolean }) {
	const className2 = classNames("zone-selection-box--corner", className, {
		"zone-selection-box--hidden": !isAllowed, // Hide with class instead of remove, so we can use fade-out animation, also faster because no repaint needed
	});

	return (
		<div className={className2}>
			<div />
		</div>
	);
}

function SideHandle({ className, isAllowed }: { className: string; isAllowed: boolean }) {
	const className2 = classNames("zone-selection-box--side", className, {
		"zone-selection-box--hidden": !isAllowed, // Hide with class instead of remove, so we can use fade-out animation, also faster because no repaint needed
	});

	return (
		<div className={className2}>
			<div />
		</div>
	);
}

export const ZoneSelectionBox = memo(
	forwardRef<IZoneSelectionBoxRef, IZoneSelectionBoxProps>(
		({ handles: allowedHandles = ZoneSelectionBoxHandlers.None }, ref) => {
			/** Ref to DOM element */
			const divRef = useRef<HTMLDivElement | null>(null);
			const isDebugSelectionBox = useFeatureFlagToggle("debugSelectionBox");
			const { platform } = useAppContext();

			/** Expose ZoneSelectionBox methods via ref */
			useImperativeHandle(
				ref,
				() => {
					const el = divRef.current;
					if (!el) return null as unknown as IZoneSelectionBoxRef;

					return {
						el,
						updateZoneSelectionBox: (
							selectedZone: eXpress.core.IZone | undefined,
							dipToScreenTransform: Transform,
						) => {
							updateZoneSelectionBox(
								selectedZone,
								el,
								dipToScreenTransform,
								platform.browserInfo,
							);
						},

						showDefaultHandlers: (
							selectedZone: eXpress.core.IZone | undefined,
							dipToScreenTransform: Transform,
							// eslint-disable-next-line @typescript-eslint/no-shadow
							allowedHandles: ZoneSelectionBoxHandlers,
						) => {
							showDefaultHandlers(
								selectedZone,
								allowedHandles,
								el,
								dipToScreenTransform,
							);
						},
					};
				},
				[platform.browserInfo],
			);

			return (
				<div
					className={`zone-selection-box ${
						isDebugSelectionBox ? "zone-selection-box--debug" : ""
					}`}
					ref={divRef}
				>
					<SideHandle
						className="zone-selection-box--top"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Top) !== 0}
					/>
					<SideHandle
						className="zone-selection-box--left"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Left) !== 0}
					/>
					<SideHandle
						className="zone-selection-box--right"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Right) !== 0}
					/>
					<SideHandle
						className="zone-selection-box--bottom"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Bottom) !== 0}
					/>

					<CornerHandle
						className="zone-selection-box--topleft"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.TopLeft) !== 0}
					/>
					<CornerHandle
						className="zone-selection-box--topright"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.TopRight) !== 0}
					/>
					<CornerHandle
						className="zone-selection-box--bottomleft"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.BottomLeft) !== 0}
					/>
					<CornerHandle
						className="zone-selection-box--bottomright"
						isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.BottomRight) !== 0}
					/>
				</div>
			);
		},
	),
);
ZoneSelectionBox.displayName = "ZoneSelectionBox";
